"use client";

import { clsx } from "clsx";
import * as m from "motion/react-m";

type ProgressBarSegmentProps = {
  isCurrent: boolean;
  isCompleted: boolean;
  trackClassName: string;
  fillClassName: string;
};

function ProgressBarSegment({ isCurrent, isCompleted, trackClassName, fillClassName }: ProgressBarSegmentProps) {
  return (
    <div className={trackClassName}>
      <m.div
        className={clsx("tw-absolute tw-inset-0 tw-origin-left", fillClassName)}
        initial={{ scaleX: isCompleted ? 1 : 0 }}
        animate={{
          scaleX: isCurrent || isCompleted ? 1 : 0,
        }}
        transition={{
          duration: isCurrent ? 0.5 : 0,
          ease: [0.4, 0.0, 0.2, 1],
          delay: isCurrent ? 0.15 : 0,
        }}
      />
    </div>
  );
}

const PROGRESS_BAR_VARIANTS = {
  default: {
    wrapperClassName: "tw-flex tw-gap-2 tw-items-center",
    trackClassName: "tw-relative tw-h-1 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-gray-300",
    fillClassName: "tw-rounded-full tw-bg-black",
    ariaHidden: false as const,
  },
  testVariant: {
    wrapperClassName: "tw-flex tw-gap-1.5 tw-items-center tw-shrink-0",
    trackClassName: "tw-relative tw-h-1 tw-w-12 tw-shrink-0 tw-overflow-hidden tw-rounded-md tw-bg-black/10",
    fillClassName: "tw-rounded-md tw-bg-black",
    ariaHidden: true as const,
  },
} as const;

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
  variant?: keyof typeof PROGRESS_BAR_VARIANTS;
}

export function ProgressBar({ currentStep, totalSteps, className, variant = "default" }: ProgressBarProps) {
  const { wrapperClassName, trackClassName, fillClassName, ariaHidden } = PROGRESS_BAR_VARIANTS[variant];
  return (
    <div className={clsx(wrapperClassName, className)} aria-hidden={ariaHidden || undefined}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <ProgressBarSegment
          key={index}
          isCurrent={index === currentStep - 1}
          isCompleted={index < currentStep - 1}
          trackClassName={trackClassName}
          fillClassName={fillClassName}
        />
      ))}
    </div>
  );
}
